import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Tooltip from '@mui/joy/Tooltip'
import IconButton from '@mui/joy/IconButton'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import Divider from '@mui/joy/Divider'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EmailIcon from '@mui/icons-material/Email'
import { CircularProgress } from '@mui/joy'
import axios from 'axios'
import { formatToUserLocalTime } from '@/utils/date-formatting'
import { AgentActivity, AgentActivityAction, AgentActivityStatus } from '@numeo/types'

interface EmailContentResponse {
    cleanedBody: string | null
    subject: string
    from: Array<{ name?: string; email: string }>
    to: Array<{ name?: string; email: string }>
    cc: Array<{ name?: string; email: string }>
    date: number
    body: string
}

const PAGE_SIZE = 50

const statusColors: Record<string, 'success' | 'danger' | 'warning' | 'neutral' | 'primary'> = {
    success: 'success',
    error: 'danger',
    warning: 'warning',
    info: 'primary',
}

const formatAction = (action: string): string => {
    return action
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}

const formatParticipants = (participants: Array<{ name?: string; email: string }>): string => {
    return participants.map((p) => (p.name ? `${p.name} <${p.email}>` : p.email)).join(', ')
}

export default function AgentActivityLogs() {
    const [rows, setRows] = React.useState<AgentActivity[]>([])
    const [page, setPage] = React.useState(1)
    const [loading, setLoading] = React.useState(true)
    const [fetching, setFetching] = React.useState(false)
    const [totalCount, setTotalCount] = React.useState(0)
    const [totalPages, setTotalPages] = React.useState(0)

    // Filter input states
    const [projectFilter, setProjectFilter] = React.useState('')
    const [entityIdFilter, setEntityIdFilter] = React.useState('')
    const [emailObjectIdFilter, setEmailObjectIdFilter] = React.useState('')
    const [actionFilter, setActionFilter] = React.useState('')
    const [statusFilter, setStatusFilter] = React.useState('')

    // Applied filters (for triggering API calls)
    const [appliedProjectFilter, setAppliedProjectFilter] = React.useState('')
    const [appliedEntityIdFilter, setAppliedEntityIdFilter] = React.useState('')
    const [appliedEmailObjectIdFilter, setAppliedEmailObjectIdFilter] = React.useState('')
    const [appliedActionFilter, setAppliedActionFilter] = React.useState('')
    const [appliedStatusFilter, setAppliedStatusFilter] = React.useState('')

    // Detail modal
    const [selectedActivity, setSelectedActivity] = React.useState<AgentActivity | null>(null)
    const [detailModalOpen, setDetailModalOpen] = React.useState(false)

    // Email content loading
    const [emailLoading, setEmailLoading] = React.useState(false)
    const [emailContent, setEmailContent] = React.useState<EmailContentResponse | null>(null)
    const [emailError, setEmailError] = React.useState<string | null>(null)

    const handleFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            applyFilters()
        }
    }

    const applyFilters = () => {
        setPage(1)
        setAppliedProjectFilter(projectFilter)
        setAppliedEntityIdFilter(entityIdFilter)
        setAppliedEmailObjectIdFilter(emailObjectIdFilter)
        setAppliedActionFilter(actionFilter)
        setAppliedStatusFilter(statusFilter)
    }

    const clearFilters = () => {
        setProjectFilter('')
        setEntityIdFilter('')
        setEmailObjectIdFilter('')
        setActionFilter('')
        setStatusFilter('')
        setAppliedProjectFilter('')
        setAppliedEntityIdFilter('')
        setAppliedEmailObjectIdFilter('')
        setAppliedActionFilter('')
        setAppliedStatusFilter('')
        setPage(1)
    }

    const openDetailModal = (activity: AgentActivity) => {
        setSelectedActivity(activity)
        setEmailContent(null)
        setEmailError(null)
        setDetailModalOpen(true)
    }

    const loadEmailContent = async () => {
        if (!selectedActivity?.emailObjectId) return

        setEmailLoading(true)
        setEmailError(null)

        try {
            const res = await axios.get(`/gluon/agent-activity-logs/email/${selectedActivity.emailObjectId}`)
            setEmailContent(res.data)
        } catch (err) {
            setEmailError('Email not found or expired')
        } finally {
            setEmailLoading(false)
        }
    }

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const isInitialLoad =
                    page === 1 && !appliedProjectFilter && !appliedEntityIdFilter && !appliedEmailObjectIdFilter && !appliedActionFilter && !appliedStatusFilter

                if (isInitialLoad) {
                    setLoading(true)
                } else {
                    setFetching(true)
                }

                const params = new URLSearchParams()
                params.append('page', page.toString())
                params.append('pageSize', PAGE_SIZE.toString())
                if (appliedProjectFilter) params.append('projectId', appliedProjectFilter)
                if (appliedEntityIdFilter) params.append('entityId', appliedEntityIdFilter)
                if (appliedEmailObjectIdFilter) params.append('emailObjectId', appliedEmailObjectIdFilter)
                if (appliedActionFilter) params.append('action', appliedActionFilter)
                if (appliedStatusFilter) params.append('status', appliedStatusFilter)

                const res = await axios.get(`/gluon/agent-activity-logs?${params.toString()}`)
                const data = res.data || {}

                setRows(Array.isArray(data.rows) ? data.rows : [])
                setTotalCount(typeof data.total === 'number' ? data.total : 0)
                setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0)
            } catch (e) {
                console.error('Failed to fetch agent activity logs:', e)
                setRows([])
                setTotalCount(0)
                setTotalPages(0)
            } finally {
                setLoading(false)
                setFetching(false)
            }
        }

        fetchData()
    }, [page, appliedProjectFilter, appliedEntityIdFilter, appliedEmailObjectIdFilter, appliedActionFilter, appliedStatusFilter])

    // Clamp page when totalPages shrinks
    React.useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(1)
        }
    }, [totalPages, page])

    const truncateText = (text: string, maxLength = 50) => {
        if (!text || text.length <= maxLength) return text || 'N/A'
        return text.substring(0, maxLength) + '...'
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading agent activity logs...</Typography>
            </Box>
        )
    }

    return (
        <React.Fragment>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    my: 2,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography level="h2" component="h1">
                        Agent Activity Logs ({totalCount})
                    </Typography>
                    {fetching && <CircularProgress size="sm" />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                        size="sm"
                        variant="outlined"
                        startDecorator={<RefreshIcon />}
                        disabled={fetching}
                        onClick={() => {
                            setPage(1)
                            setAppliedProjectFilter(projectFilter)
                        }}
                    >
                        Refresh
                    </Button>
                    <Button size="sm" variant="outlined" disabled={page === 1 || fetching} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages || 1}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page >= totalPages || fetching} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Box
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        width: '100%',
                        alignItems: 'flex-end',
                    }}
                >
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Project Name</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search project..."
                            startDecorator={<SearchIcon />}
                            value={projectFilter}
                            onChange={(e) => setProjectFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Entity ID</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Exact match..."
                            value={entityIdFilter}
                            onChange={(e) => setEntityIdFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Email Object ID</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Exact match..."
                            value={emailObjectIdFilter}
                            onChange={(e) => setEmailObjectIdFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Action</FormLabel>
                        <Select size="sm" placeholder="All actions" value={actionFilter} onChange={(_, val) => setActionFilter(val || '')}>
                            <Option value="">All</Option>
                            {Object.values(AgentActivityAction).map((action) => (
                                <Option key={action} value={action}>
                                    {formatAction(action)}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Status</FormLabel>
                        <Select size="sm" placeholder="All statuses" value={statusFilter} onChange={(_, val) => setStatusFilter(val || '')}>
                            <Option value="">All</Option>
                            {Object.values(AgentActivityStatus).map((status) => (
                                <Option key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-end' }}>
                        <Button size="sm" variant="solid" color="primary" onClick={applyFilters}>
                            Apply
                        </Button>
                        <Button size="sm" variant="outlined" color="neutral" onClick={clearFilters}>
                            Clear
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Table */}
            <Sheet
                variant="outlined"
                sx={{
                    display: { xs: 'none', sm: 'initial' },
                    width: '100%',
                    borderRadius: 'sm',
                    flexShrink: 1,
                    overflow: 'auto',
                    minHeight: 0,
                }}
            >
                <Table
                    aria-labelledby="agent-activity-logs-table-title"
                    stickyHeader
                    hoverRow
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                        '--TableCell-paddingY': '4px',
                        '--TableCell-paddingX': '8px',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 50, padding: '12px 6px' }}>Actions</th>
                            <th style={{ width: 150, padding: '12px 6px' }}>Project</th>
                            <th style={{ width: 100, padding: '12px 6px' }}>Entity Type</th>
                            <th style={{ width: 150, padding: '12px 6px' }}>Entity ID</th>
                            <th style={{ width: 180, padding: '12px 6px' }}>Action</th>
                            <th style={{ width: 100, padding: '12px 6px' }}>Status</th>
                            <th style={{ width: 250, padding: '12px 6px' }}>Title</th>
                            <th style={{ width: 160, padding: '12px 6px' }}>Created At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={row.id || index}>
                                <td>
                                    <Tooltip title="View Details">
                                        <IconButton size="sm" variant="plain" onClick={() => openDetailModal(row)}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Tooltip title={row.projectId}>
                                        <Typography level="body-xs" sx={{ cursor: 'default' }}>
                                            {truncateText(row.projectId, 20)}
                                        </Typography>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.entityType || 'N/A'}</Typography>
                                </td>
                                <td>
                                    <Tooltip title={row.entityId || 'N/A'}>
                                        <Typography level="body-xs" sx={{ cursor: 'default', fontFamily: 'monospace' }}>
                                            {truncateText(row.entityId || '', 18)}
                                        </Typography>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Typography level="body-xs">{formatAction(row.action)}</Typography>
                                </td>
                                <td>
                                    <Chip size="sm" variant="soft" color={statusColors[row.status] || 'neutral'}>
                                        {row.status}
                                    </Chip>
                                </td>
                                <td>
                                    <Tooltip title={row.title}>
                                        <Typography level="body-xs" sx={{ cursor: 'default' }}>
                                            {truncateText(row.title, 35)}
                                        </Typography>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.createdAt ? formatToUserLocalTime(row.createdAt.toString()) : 'N/A'}</Typography>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={8} style={{ textAlign: 'center', padding: '32px' }}>
                                    <Typography level="body-sm" color="neutral">
                                        No agent activity logs found
                                    </Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Sheet>

            {/* Detail Modal */}
            <Modal open={detailModalOpen} onClose={() => setDetailModalOpen(false)}>
                <ModalDialog sx={{ width: 800, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>
                        Agent Activity Details
                    </Typography>

                    {selectedActivity && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Status & Action Row */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Status
                                    </Typography>
                                    <Chip size="sm" variant="soft" color={statusColors[selectedActivity.status] || 'neutral'}>
                                        {selectedActivity.status}
                                    </Chip>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Action
                                    </Typography>
                                    <Typography level="body-sm">{formatAction(selectedActivity.action)}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Agent Type
                                    </Typography>
                                    <Typography level="body-sm">{selectedActivity.agentType}</Typography>
                                </Box>
                            </Box>

                            {/* IDs Row */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Activity ID
                                    </Typography>
                                    <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                        {selectedActivity.id}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Project ID
                                    </Typography>
                                    <Typography level="body-sm">{selectedActivity.projectId}</Typography>
                                </Box>
                            </Box>

                            {/* Entity Info */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Entity Type
                                    </Typography>
                                    <Typography level="body-sm">{selectedActivity.entityType || 'N/A'}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Entity ID
                                    </Typography>
                                    <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                        {selectedActivity.entityId || 'N/A'}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Email Object ID
                                    </Typography>
                                    <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                        {selectedActivity.emailObjectId || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Title */}
                            <Box>
                                <Typography level="body-sm" fontWeight="bold">
                                    Title
                                </Typography>
                                <Typography level="body-sm">{selectedActivity.title}</Typography>
                            </Box>

                            {/* Description */}
                            {selectedActivity.description && (
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Description
                                    </Typography>
                                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedActivity.description}
                                        </Typography>
                                    </Sheet>
                                </Box>
                            )}

                            {/* Metadata */}
                            {selectedActivity.metadata && Object.keys(selectedActivity.metadata).length > 0 && (
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Metadata
                                    </Typography>
                                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', maxHeight: 200, overflow: 'auto' }}>
                                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(selectedActivity.metadata, null, 2)}
                                        </Typography>
                                    </Sheet>
                                </Box>
                            )}

                            {/* Timestamp */}
                            <Box>
                                <Typography level="body-sm" fontWeight="bold">
                                    Created At
                                </Typography>
                                <Typography level="body-sm">
                                    {selectedActivity.createdAt ? formatToUserLocalTime(selectedActivity.createdAt.toString()) : 'N/A'}
                                </Typography>
                            </Box>

                            {/* Email Content Section */}
                            {selectedActivity.emailObjectId && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography level="title-md">Email Content</Typography>
                                            {!emailContent && !emailLoading && (
                                                <Button size="sm" variant="outlined" startDecorator={<EmailIcon />} onClick={loadEmailContent}>
                                                    Load Email
                                                </Button>
                                            )}
                                            {emailLoading && <CircularProgress size="sm" />}
                                        </Box>

                                        {emailError && (
                                            <Sheet variant="soft" color="warning" sx={{ p: 1.5, borderRadius: 'sm' }}>
                                                <Typography level="body-sm" color="warning">
                                                    {emailError}
                                                </Typography>
                                            </Sheet>
                                        )}

                                        {emailContent && (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                <Box>
                                                    <Typography level="body-sm" fontWeight="bold">
                                                        Subject
                                                    </Typography>
                                                    <Typography level="body-sm">{emailContent.subject || 'N/A'}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                                        <Typography level="body-sm" fontWeight="bold">
                                                            From
                                                        </Typography>
                                                        <Typography level="body-xs">{formatParticipants(emailContent.from)}</Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                                        <Typography level="body-sm" fontWeight="bold">
                                                            To
                                                        </Typography>
                                                        <Typography level="body-xs">{formatParticipants(emailContent.to)}</Typography>
                                                    </Box>
                                                </Box>
                                                {emailContent.cc && emailContent.cc.length > 0 && (
                                                    <Box>
                                                        <Typography level="body-sm" fontWeight="bold">
                                                            CC
                                                        </Typography>
                                                        <Typography level="body-xs">{formatParticipants(emailContent.cc)}</Typography>
                                                    </Box>
                                                )}
                                                <Box>
                                                    <Typography level="body-sm" fontWeight="bold">
                                                        Date
                                                    </Typography>
                                                    <Typography level="body-sm">
                                                        {emailContent.date ? formatToUserLocalTime(new Date(emailContent.date * 1000).toISOString()) : 'N/A'}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography level="body-sm" fontWeight="bold">
                                                        Cleaned Body
                                                    </Typography>
                                                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', maxHeight: 300, overflow: 'auto' }}>
                                                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px' }}>
                                                            {emailContent.cleanedBody || emailContent.body || 'No content available'}
                                                        </Typography>
                                                    </Sheet>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </ModalDialog>
            </Modal>
        </React.Fragment>
    )
}
